import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../lib/api";

import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";

export default function ApplicationForm() {
  const [type, setType] = useState("");
  const [showIndividualNotice, setShowIndividualNotice] = useState(false);

  const [institutionName, setInstitutionName] = useState("");
  const [deanName, setDeanName] = useState("");
  const [legacyName, setLegacyName] = useState("");
  const [institutionLocation, setInstitutionLocation] = useState("");
  const [institutionCertType, setInstitutionCertType] = useState("");
  const [statement, setStatement] = useState("");
  const [oocNotes, setOocNotes] = useState("");
  const [file, setFile] = useState(null);

  const [businessName, setBusinessName] = useState("");
  const [ownerName, setOwnerName] = useState("");
  const [businessLocation, setBusinessLocation] = useState("");
  const [businessCertType, setBusinessCertType] = useState("");

  const handleTypeChange = (v) => {
    if (v === "Individual") {
      setShowIndividualNotice(true);
      setType("");
      return;
    }
    setType(v);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!type) return toast.error("Please select request type");
    if (type === "Institution" && (!institutionName || !deanName || !institutionCertType)) return toast.error("Please complete all required Institution fields");
    if (type === "Business" && (!businessName || !ownerName || !businessCertType)) return toast.error("Please complete all required Business fields");

    try {
      const fd = new FormData();
      fd.append("applicant_type", type);
      const payload = type === "Institution" ? {
        institution_name: institutionName,
        dean_name: deanName,
        sl_legacy_name: legacyName,
        institution_location: institutionLocation,
        certification_type: institutionCertType,
        statement_of_intent: statement,
        ooc_notes: oocNotes,
      } : {
        business_name: businessName,
        owner_name: ownerName,
        sl_legacy_name: legacyName,
        business_location: businessLocation,
        certification_type: businessCertType,
        statement_of_intent: statement,
        ooc_notes: oocNotes,
      };
      fd.append("form", JSON.stringify(payload));
      if (file) fd.append("proof_file", file);

      const res = await axios.post(`${API}/applications`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Application submitted. Status: " + res.data.status);
      setType("");
      setInstitutionName(""); setDeanName(""); setLegacyName(""); setInstitutionLocation("");
      setInstitutionCertType(""); setStatement(""); setOocNotes(""); setFile(null);
      setBusinessName(""); setOwnerName(""); setBusinessLocation(""); setBusinessCertType("");
    } catch (err) {
      console.error(err);
      toast.error("Submission failed. Please try again");
    }
  };

  return (
    <div>
      <div className="text-center">
        <span className="badge-red">Official Potion Request</span>
        <h2 className="section-title text-3xl guild-display">Apply for Potion Certification</h2>
        <p className="text-yellow-100/80 mt-2 max-w-2xl mx-auto">The Academic Guild exists to ensure corruption does not spread through misuse of knowledge. Only accredited institutions may issue certifications.</p>
      </div>

      <Card className="guild-card mt-6">
        <CardHeader>
          <CardTitle className="text-xl">Application Form</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="col-span-1 md:col-span-2">
              <Label className="text-white">Please select which suits your request</Label>
              <Select value={type} onValueChange={handleTypeChange}>
                <SelectTrigger className="w-full mt-2 guild-input">
                  <SelectValue placeholder="Choose one" />
                </SelectTrigger>
                <SelectContent className="bg-[#2a0c0c] border-yellow-500/30 text-yellow-100">
                  <SelectGroup>
                    <SelectLabel>Type</SelectLabel>
                    <SelectItem value="Institution">Institution</SelectItem>
                    <SelectItem value="Business">Business</SelectItem>
                    <SelectItem value="Individual">Individual</SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>

            {type === "Institution" && (
              <>
                <div>
                  <Label className="text-white">Institution Name (IC)</Label>
                  <Input className="mt-2 guild-input" value={institutionName} onChange={(e) => setInstitutionName(e.target.value)} placeholder="e.g., Arcanum Conservatory" />
                </div>
                <div>
                  <Label className="text-white">Dean / Responsible Party Name (IC)</Label>
                  <Input className="mt-2 guild-input" value={deanName} onChange={(e) => setDeanName(e.target.value)} placeholder="e.g., Dean Valeris" />
                </div>
                <div>
                  <Label className="text-white">Second Life Legacy Name (OOC)</Label>
                  <Input className="mt-2 guild-input" value={legacyName} onChange={(e) => setLegacyName(e.target.value)} placeholder="your SL legacy name" />
                </div>
                <div>
                  <Label className="text-white">Location of Institution (SLURL)</Label>
                  <Input className="mt-2 guild-input" value={institutionLocation} onChange={(e) => setInstitutionLocation(e.target.value)} placeholder="SLURL" />
                </div>
                <div>
                  <Label className="text-white">Certification Type Requested</Label>
                  <Select value={institutionCertType} onValueChange={setInstitutionCertType}>
                    <SelectTrigger className="w-full mt-2 guild-input">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#2a0c0c] border-yellow-500/30 text-yellow-100">
                      <SelectItem value="Sales">Sales</SelectItem>
                      <SelectItem value="Teaching">Teaching</SelectItem>
                      <SelectItem value="Research">Research</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2">
                  <Label className="text-white">Proof of 101 Base Course Completion</Label>
                  <input className="mt-2 w-full p-2 guild-input rounded" type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-white">Statement of Intent (IC justification)</Label>
                  <Textarea className="mt-2 guild-input" rows={4} value={statement} onChange={(e) => setStatement(e.target.value)} placeholder="Short IC justification" />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-white">OOC Documentation Notes (hidden, staff only)</Label>
                  <Textarea className="mt-2 guild-input" rows={3} value={oocNotes} onChange={(e) => setOocNotes(e.target.value)} placeholder="Any OOC notes for staff reviewers (not public)" />
                </div>
              </>
            )}

            {type === "Business" && (
              <>
                <div>
                  <Label className="text-white">Business Name (IC)</Label>
                  <Input className="mt-2 guild-input" value={businessName} onChange={(e) => setBusinessName(e.target.value)} placeholder="e.g., Crimson Cauldron Co." />
                </div>
                <div>
                  <Label className="text-white">Owner / Responsible Party Name (IC)</Label>
                  <Input className="mt-2 guild-input" value={ownerName} onChange={(e) => setOwnerName(e.target.value)} placeholder="e.g., Maris Aleron" />
                </div>
                <div>
                  <Label className="text-white">Second Life Legacy Name (OOC)</Label>
                  <Input className="mt-2 guild-input" value={legacyName} onChange={(e) => setLegacyName(e.target.value)} placeholder="your SL legacy name" />
                </div>
                <div>
                  <Label className="text-white">Location of Operations (SLURL)</Label>
                  <Input className="mt-2 guild-input" value={businessLocation} onChange={(e) => setBusinessLocation(e.target.value)} placeholder="SLURL" />
                </div>
                <div>
                  <Label className="text-white">Certification Type Requested</Label>
                  <Select value={businessCertType} onValueChange={setBusinessCertType}>
                    <SelectTrigger className="w-full mt-2 guild-input">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#2a0c0c] border-yellow-500/30 text-yellow-100">
                      <SelectItem value="Sales">Sales</SelectItem>
                      <SelectItem value="Research">Research</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2">
                  <Label className="text-white">Statement of Intent</Label>
                  <Textarea className="mt-2 guild-input" rows={4} value={statement} onChange={(e) => setStatement(e.target.value)} placeholder="Short IC justification" />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-white">OOC Documentation Notes (hidden, staff only)</Label>
                  <Textarea className="mt-2 guild-input" rows={3} value={oocNotes} onChange={(e) => setOocNotes(e.target.value)} placeholder="Any OOC notes for staff reviewers (not public)" />
                </div>
              </>
            )}

            <div className="col-span-1 md:col-span-2 flex items-center justify-between mt-2">
              <div className="flex items-center gap-2">
                <span className="badge-gold">Accredited standards</span>
              </div>
              <div className="flex gap-2">
                <Button type="submit" className="btn-slate" disabled={!type}>Submit application</Button>
              </div>
            </div>
          </form>
        </CardContent>
      </Card>

      <Dialog open={showIndividualNotice} onOpenChange={setShowIndividualNotice}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Notice</DialogTitle>
            <DialogDescription>
              We do not support potion certifications for individuals. Players are expected to obtain their potions ICly from certified Institutions or Businesses.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => { setShowIndividualNotice(false); }}>Understood</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}